/**
 * Priority order for searching product ID fields.
 * Fields in the same array have equal priority.
 * Arrays are checked in order from highest to lowest priority.
 */
const PRODUCT_ID_PRIORITY = [
  ['productGroupId', 'productId'],
  ['asin', 'bsin'],
  ['gtin', 'gtin8', 'gtin12', 'gtin13', 'gtin14', 'upc'],
  ['mpn'],
  ['sku'],
] as const

export interface ProductIdResult {
  /** The product ID value found */
  value: string | null
  /** The field name where the ID was found */
  field: string | null
}

type LdJsonData = Record<string, unknown>

/**
 * Validates if a value is a valid product ID (non-empty string or number)
 */
function isValidProductId(value: unknown): value is string | number {
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  if (typeof value === 'number') {
    return true
  }
  return false
}

/**
 * Parses a single ld+json script element and returns the data as an array of objects
 */
function parseLdJsonScript(script: Element): LdJsonData[] {
  try {
    const data = JSON.parse(script.textContent || '')

    // ld+json can contain either a single object or an array of objects
    if (Array.isArray(data)) {
      return data.filter((item) => item && typeof item === 'object')
    }

    if (data && typeof data === 'object') {
      return [data]
    }
  } catch {
    // Ignore parsing errors for malformed JSON
  }

  return []
}

/**
 * Collects and parses all ld+json scripts from the document
 */
function getAllLdJsonData(): LdJsonData[] {
  const scripts = document.querySelectorAll(
    'script[type="application/ld+json"]',
  )
  const allData: LdJsonData[] = []

  scripts.forEach((script) => {
    const parsedData = parseLdJsonScript(script)
    allData.push(...parsedData)
  })

  return allData
}

/**
 * Gets a value from an object by field name, case-insensitive.
 * First tries exact match for performance, then tries case-insensitive match.
 */
function getValueCaseInsensitive(
  data: LdJsonData,
  targetField: string,
): { value: unknown; actualField: string } | null {
  // Try exact match first (faster)
  if (targetField in data) {
    return {
      value: data[targetField],
      actualField: targetField,
    }
  }

  // Try case-insensitive match
  const lowerTargetField = targetField.toLowerCase()
  for (const [key, value] of Object.entries(data)) {
    if (key.toLowerCase() === lowerTargetField) {
      return {
        value,
        actualField: key,
      }
    }
  }

  return null
}

/**
 * Searches for a product ID across all data objects for a specific field.
 * Field matching is case-insensitive.
 */
function findProductIdByField(
  allData: LdJsonData[],
  field: string,
): ProductIdResult | null {
  for (const data of allData) {
    const result = getValueCaseInsensitive(data, field)

    if (result && isValidProductId(result.value)) {
      return {
        value: String(result.value),
        field: result.actualField,
      }
    }
  }

  return null
}

/**
 * Extracts product ID from all ld+json scripts in the document.
 *
 * Searches based on priority:
 * 1. productGroupId = productId (highest)
 * 2. asin = bsin
 * 3. sku
 * 4. gtin* = upc (gtin, gtin8, gtin12, gtin13, gtin14, upc)
 * 5. mpn (lowest)
 *
 * For each priority level, searches through all ld+json data objects
 * before moving to the next priority level.
 *
 * @returns Object containing the product ID value and the field name where it was found,
 *          or null values if no product ID was found
 */
export function extractProductIdFromLdJson(): ProductIdResult {
  const allData = getAllLdJsonData()

  // Search by priority: check all data for highest priority fields first
  for (const fieldGroup of PRODUCT_ID_PRIORITY) {
    for (const field of fieldGroup) {
      const result = findProductIdByField(allData, field)
      if (result) {
        return result
      }
    }
  }

  // No product ID found
  return { value: null, field: null }
}
